import 'ol/ol.css';
import { Vector as VectorLayer } from 'ol/layer'
import { Vector as VectorSource } from 'ol/source'
import { Style, Stroke, Fill, Text, RegularShape, Icon } from 'ol/style.js';
import GeoJSON from "ol/format/GeoJSON";
import Point from "ol/geom/Point";
import { setMapSingleEventHandler } from 'map/OlExtension'
import { Circle as CircleStyle } from 'ol/style';
import { blueGrey, cyan, indigo, deepPurple, teal, purple } from '@mui/material/colors';

export default class IntersectionPointLayer extends VectorLayer {
    styleNormal = new Style({
        image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
                color: 'rgba(255, 0, 0, 255)',
                width: 4,
            }),
        }),
    });
    styleFocus = new Style({
        image: new CircleStyle({
            radius: 32,
            stroke: new Stroke({
                color: 'rgba(255, 0, 0, 255)',
                width: 4,
            }),
            scale: [0.3, 0.3]
        }),
    });
    styleBorder = new Style({
        image: new CircleStyle({
            radius: 32,
            stroke: new Stroke({
                color: 'rgba(255, 0, 0, 255)',
                width: 4,
            }),
            scale: [0.3, 0.3]
        }),
    });
    styleTooltip = new Style({
        text: new Text({
            font: "12px Calibri,sans-serif",
            fill: new Fill({
                color: "rgba(255, 255, 255, 1)"
            }),
            backgroundFill: new Fill({
                color: "rgba(0, 0, 0, 0.7)"
            }),
            padding: [3, 3, 2, 6],
            textBaseline: "bottom",
            offsetY: -15
        }),
        image: new RegularShape({
            radius: 6,
            points: 3,
            angle: Math.PI,
            displacement: [0, 10],
            fill: new Fill({
                color: "rgba(0, 0, 0, 0.7)"
            })
        }),
        geometry: new Point([]),
        zIndex: 1
    });

    constructor(map, data, mapProjection) {
        super({
            name: 'intersection',
            source: new VectorSource({
                features: new GeoJSON({ featureProjection: mapProjection }).readFeatures(data),
                // url: '/api/GeoJson/LINK2',
                // format: new GeoJSON()
            }),
            style: (feature) => this.styleFunction(feature)
        });
        this.map = map;
        this.selectedId = null;
    }
    clearSelection = () => {
        this.selectedId = null;
    }
    styleFunction = function (feature) {
        return this.getIntersectionStyle(feature);
    };

    getIntersectionStyle = (feature) => {
        const text = feature.get('crsrdNm');
        const zoom = Math.ceil(this.map.getView().getZoom());
        let scaleValue = (zoom - 13) * 0.20;
        if (scaleValue < 0.1)
            scaleValue = 0.1;
        return new Style({
            image: new CircleStyle({
                radius: 32,
                fill: new Fill({
                    color: indigo[400],
                }),
                stroke: new Stroke({
                    color: indigo[600],
                    width: 1,
                }),
                scale: [scaleValue, scaleValue]
            }),
            text: zoom > 14 ? new Text({
                text: text,
                font: `bold ${zoom+2}px Calibri,sans-serif`,
                textBaseline: 'top',
                fill: new Fill({
                    color: 'black',
                }),
                stroke: new Stroke({
                    color: 'white',
                    width: 1,
                }),
                scale: [0.8, 1]
            }) : undefined,
        });
    }

    setTooltipMode = (isEnable) => {
        setMapSingleEventHandler(this.map, this, 'pointermove', isEnable ? async (e, feature) => {
            const map = this.map;
            if (feature) {
                const crsrdId = feature.get('crsrdId');
                const crsrdNm = feature.get('crsrdNm');
                const trf = feature.get('trf');
                const dt = feature.get('dt');
                map.currentTooltipType = 'HOVER';
                map.lastTooltipId = crsrdId;
                
                let dateText = "";
                if (dt) {
                    const dateObj = new Date(dt);
                    const yyyy = dateObj.getFullYear();
                    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const dd = String(dateObj.getDate()).padStart(2, '0');
                    const hh = String(dateObj.getHours()).padStart(2, '0');
                    dateText = `${yyyy}년 ${mm}월 ${dd}일 ${hh}시 기준`;
                } else {
                    dateText = "기준 일시 정보 없음";
                }

                let displayVol = "정보 없음";
                if (trf !== null && trf !== undefined) {
                    displayVol = `${Number(trf).toLocaleString()} 대`;
                }

                const tooltipText = `교차로명 : ${crsrdNm}\n교통량 : ${displayVol}\n${dateText}`;
                this.map.tooltipLayer?.setTooltip(e.coordinate, tooltipText, feature);
            } else {
                if (map.currentTooltipType === 'HOVER') {
                    this.map.tooltipLayer?.unsetTooltip();
                    map.currentTooltipType = null;
                    map.lastTooltipId = null;
                }
            }
        } : undefined);
    }

    setLink2SelectedMode = (isEnable, link2Layer) => {
        setMapSingleEventHandler(this.map, this, 'click', isEnable ? (e, feature) => {
            // const crsrdNm = feature.get('crsrdNm');
            // const lng = feature.get('lng');
            // const lat = feature.get('lat');
            // console.log(`{name:${crsrdNm}, x:${lng}, y:${lat}}`);
            link2Layer.intersectionSelect(feature.get('crsrdId'), false)
        } : undefined);
    }

    setOnChangeSelectBind = (callback) => {
        setMapSingleEventHandler(this.map, this, 'click', (e, feature) => {
            if (feature) {
                const properties = feature.getProperties();
                const id = properties.crsrdId;
                if (this.selectedId === id) {
                    this.selectedId = null;
                    callback({ features: {}, count: 0, length: 0 });
                    return;
                }
                this.selectedId = id;
                const name = properties.crsrdNm;
                const coordinates = feature.getGeometry().getCoordinates();
                const selectResult = {
                    features: {
                        [id]: {
                            data: properties,
                            get: (key) => {
                                const mapping = {
                                    'name': name,
                                    'id': id,
                                    'lng': properties.GISPOS_X || properties.lng,
                                    'lat': properties.GISPOS_Y || properties.lat
                                };
                                return mapping[key] || feature.get(key);
                            }
                        }
                    },
                    count: 1,
                    length: 0,
                    action: {
                        type: 'MOVE_AND_TOOLTIP',
                        id: id,
                        name: name,
                        coord: coordinates // OpenLayers 좌표 [x, y]
                    }
                };
                callback(selectResult);
            } else {
                this.selectedId = null;
                callback({ features: {}, count: 0, length: 0 });
            }
        });
    }
}
